// Supabase sync layer: persists localStorage data to the database
// Uses localStorage for sync reads, Supabase for durable persistence
import { supabase } from "@/integrations/supabase/client";
import { safeJsonParse } from "./safeJson";

const DEVICE_ID_KEY = "screentime-device-id";
const STATES_KEY = "screentime-states";
const CREWS_KEY = "screentime-crews";
const CREW_MEMBERSHIP_KEY = "screentime-crew-membership";

function mergeLocalMap(storageKey: string, values: Record<string, unknown>, useTimestampGuard = false) {
  const existing = safeJsonParse<Record<string, unknown>>(localStorage.getItem(storageKey), {});
  if (useTimestampGuard) {
    // Only overwrite if remote data is newer than local
    for (const [key, remoteVal] of Object.entries(values)) {
      const localVal = existing[key] as any;
      const localTs = localVal?._localUpdatedAt || 0;
      const remoteTs = (remoteVal as any)?._localUpdatedAt || 0;
      if (remoteTs >= localTs) {
        existing[key] = remoteVal;
      }
    }
    localStorage.setItem(storageKey, JSON.stringify(existing));
  } else {
    localStorage.setItem(storageKey, JSON.stringify({ ...existing, ...values }));
  }
}

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Gets the current Clerk user's Supabase UUID via the bridge.
 * Returns null if Clerk isn't loaded yet or user is signed out — in that case
 * RLS-gated writes should be skipped (legacy device-id-only flow is dead).
 */
function getCurrentUserUuid(): string | null {
  try {
    const clerk = (typeof window !== "undefined" && (window as any).Clerk) || null;
    return clerk?.user?.externalId ?? null;
  } catch {
    return null;
  }
}

// Fire-and-forget upsert helpers
export async function syncProfiles(profiles: unknown[], pin: string): Promise<void> {
  const deviceId = getDeviceId();
  const userId = getCurrentUserUuid();
  if (!userId) return; // RLS requires user_id; skip if no Clerk session
  const { error } = await supabase
    .from("parent_settings")
    .upsert(
      { user_id: userId, device_id: deviceId, profiles: profiles as any, pin },
      { onConflict: "user_id" }
    );
  if (error) console.warn("syncProfiles error:", error.message);
}

export function syncConfig(childId: string, configData: unknown) {
  // RLS on child_configs requires current_user_uuid() = parent_id of the
  // child. Skip silently when there's no Clerk session, otherwise the write
  // would 403 and pollute the console (LJ flagged repeatedly).
  if (!getCurrentUserUuid()) return;
  supabase
    .from("child_configs")
    .upsert({ child_id: childId, config_data: configData as any }, { onConflict: "child_id" })
    .then(({ error }) => { if (error) console.warn("syncConfig error:", error.message); });
}

export function syncState(childId: string, stateData: unknown) {
  // Same RLS constraint as syncConfig — gate on Clerk session.
  if (!getCurrentUserUuid()) return;
  supabase
    .from("child_states")
    .upsert({ child_id: childId, state_data: stateData as any }, { onConflict: "child_id" })
    .then(({ error }) => { if (error) console.warn("syncState error:", error.message); });
}

export function syncDeleteChild(childId: string) {
  Promise.all([
    supabase.from("child_configs").delete().eq("child_id", childId),
    supabase.from("child_states").delete().eq("child_id", childId),
  ]).catch(() => {});
}

export function syncCrewData(crewId: string, crewJson: unknown) {
  supabase
    .from("crew_data")
    .upsert({ crew_id: crewId, crew_json: crewJson as any }, { onConflict: "crew_id" })
    .then(({ error }) => { if (error) console.warn("syncCrewData error:", error.message); });
}

export function syncCrewMembership(childId: string, crewIds: string[]) {
  supabase
    .from("crew_memberships")
    .upsert({ child_id: childId, crew_ids: crewIds as any }, { onConflict: "child_id" })
    .then(({ error }) => { if (error) console.warn("syncCrewMembership error:", error.message); });
}

export async function refreshChildState(childId: string): Promise<void> {
  try {
    const { data, error } = await supabase
      .from("child_states")
      .select("child_id, state_data")
      .eq("child_id", childId)
      .maybeSingle();

    if (error) {
      console.warn("refreshChildState error:", error.message);
      return;
    }

    if (data) {
      mergeLocalMap(STATES_KEY, { [data.child_id]: data.state_data }, true);
    }
  } catch (err) {
    console.warn("refreshChildState error:", err);
  }
}

export async function refreshNotificationData(): Promise<void> {
  try {
    const [
      { data: states, error: statesError },
      { data: crews, error: crewsError },
      { data: memberships, error: membershipsError },
    ] = await Promise.all([
      supabase.from("child_states").select("child_id, state_data"),
      supabase.from("crew_data").select("crew_id, crew_json"),
      supabase.from("crew_memberships").select("child_id, crew_ids"),
    ]);

    if (statesError) console.warn("refreshNotificationData states error:", statesError.message);
    if (crewsError) console.warn("refreshNotificationData crews error:", crewsError.message);
    if (membershipsError) console.warn("refreshNotificationData memberships error:", membershipsError.message);

    if (states?.length) {
      mergeLocalMap(
        STATES_KEY,
        Object.fromEntries(states.map((state) => [state.child_id, state.state_data])),
        true,
      );
    }

    if (crews?.length) {
      mergeLocalMap(
        CREWS_KEY,
        Object.fromEntries(crews.map((crew) => [crew.crew_id, crew.crew_json])),
      );
    }

    if (memberships?.length) {
      mergeLocalMap(
        CREW_MEMBERSHIP_KEY,
        Object.fromEntries(memberships.map((membership) => [membership.child_id, membership.crew_ids])),
      );
    }
  } catch (err) {
    console.warn("[Sync] Failed to refresh notification data:", err);
  }
}

// Pull from Supabase into localStorage on init
export async function initFromSupabase(): Promise<void> {
  const deviceId = getDeviceId();

  try {
    // Pull parent settings (profiles + PIN) for this device
    const { data: parentData } = await supabase
      .from("parent_settings")
      .select("profiles, pin")
      .eq("device_id", deviceId)
      .maybeSingle();

    if (parentData) {
      if (parentData.profiles && (parentData.profiles as any[]).length > 0) {
        localStorage.setItem("screentime-profiles", JSON.stringify(parentData.profiles));
      }
      if (parentData.pin) {
        localStorage.setItem("screentime-pin", parentData.pin);
      }
    }

    // Derive child IDs from profiles so we only fetch relevant rows
    const profiles = parentData?.profiles as any[] | null;
    const childIds: string[] = (profiles || []).map((p: any) => p.id).filter(Boolean);

    if (childIds.length === 0) {
      console.log("[Sync] No child profiles found, skipping child data fetch");
      return;
    }

    // Pull only this family's child configs, states, and crew memberships
    const [
      { data: configs },
      { data: states },
      { data: memberships },
    ] = await Promise.all([
      supabase.from("child_configs").select("child_id, config_data").in("child_id", childIds),
      supabase.from("child_states").select("child_id, state_data").in("child_id", childIds),
      supabase.from("crew_memberships").select("child_id, crew_ids").in("child_id", childIds),
    ]);

    if (configs && configs.length > 0) {
      const configMap: Record<string, unknown> = {};
      const existing = safeJsonParse<Record<string, unknown>>(localStorage.getItem("screentime-configs"), {});
      for (const c of configs) {
        configMap[c.child_id] = c.config_data;
      }
      localStorage.setItem("screentime-configs", JSON.stringify({ ...existing, ...configMap }));
    }

    if (states && states.length > 0) {
      const stateMap: Record<string, unknown> = {};
      for (const s of states) {
        stateMap[s.child_id] = s.state_data;
      }
      mergeLocalMap(STATES_KEY, stateMap, true);
    }

    // Derive crew IDs from memberships, then fetch only those crews
    if (memberships && memberships.length > 0) {
      const memMap: Record<string, string[]> = {};
      const allCrewIds: string[] = [];
      for (const m of memberships) {
        const ids = m.crew_ids as string[];
        memMap[m.child_id] = ids;
        allCrewIds.push(...ids);
      }
      const existing = safeJsonParse<Record<string, unknown>>(localStorage.getItem("screentime-crew-membership"), {});
      localStorage.setItem("screentime-crew-membership", JSON.stringify({ ...existing, ...memMap }));

      const uniqueCrewIds = [...new Set(allCrewIds)];
      if (uniqueCrewIds.length > 0) {
        const { data: crews } = await supabase.from("crew_data").select("crew_id, crew_json").in("crew_id", uniqueCrewIds);
        if (crews && crews.length > 0) {
          const crewMap: Record<string, unknown> = {};
          for (const c of crews) {
            crewMap[c.crew_id] = c.crew_json;
          }
          const existingCrews = safeJsonParse<Record<string, unknown>>(localStorage.getItem("screentime-crews"), {});
          localStorage.setItem("screentime-crews", JSON.stringify({ ...existingCrews, ...crewMap }));
        }
      }
    }

    console.log("[Sync] Data loaded from database");
  } catch (err) {
    console.warn("[Sync] Failed to load from database, using localStorage:", err);
  }
}

// Push ALL localStorage data to Supabase (initial migration)
export async function pushLocalToSupabase(): Promise<void> {
  const deviceId = getDeviceId();

  const profiles = safeJsonParse<any[]>(localStorage.getItem("screentime-profiles"), []);
  const pin = localStorage.getItem("screentime-pin") || "";

  if (profiles.length > 0 || pin) {
    await supabase
      .from("parent_settings")
      .upsert({ device_id: deviceId, profiles, pin }, { onConflict: "device_id" });
  }

  const configsRaw = localStorage.getItem("screentime-configs");
  if (configsRaw) {
    const configs = safeJsonParse<Record<string, unknown>>(configsRaw, {});
    for (const [childId, configData] of Object.entries(configs)) {
      await supabase
        .from("child_configs")
        .upsert({ child_id: childId, config_data: configData as any }, { onConflict: "child_id" });
    }
  }

  const statesRaw = localStorage.getItem("screentime-states");
  if (statesRaw) {
    const states = safeJsonParse<Record<string, unknown>>(statesRaw, {});
    for (const [childId, stateData] of Object.entries(states)) {
      await supabase
        .from("child_states")
        .upsert({ child_id: childId, state_data: stateData as any }, { onConflict: "child_id" });
    }
  }

  const crewsRaw = localStorage.getItem("screentime-crews");
  if (crewsRaw) {
    const crews = safeJsonParse<Record<string, unknown>>(crewsRaw, {});
    for (const [crewId, crewJson] of Object.entries(crews)) {
      await supabase
        .from("crew_data")
        .upsert({ crew_id: crewId, crew_json: crewJson as any }, { onConflict: "crew_id" });
    }
  }

  const memRaw = localStorage.getItem("screentime-crew-membership");
  if (memRaw) {
    const memberships = safeJsonParse<Record<string, unknown>>(memRaw, {});
    for (const [childId, crewIds] of Object.entries(memberships)) {
      await supabase
        .from("crew_memberships")
        .upsert({ child_id: childId, crew_ids: crewIds as any }, { onConflict: "child_id" });
    }
  }

  localStorage.setItem("screentime-synced", "true");
  console.log("[Sync] Local data pushed to database");
}
